import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Clock, Loader2, ArrowRight, LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../lib/apiClient';
import './InviteAccept.css';

const ROLE_LABELS = {
  palo_user: 'Palo Alto Networks',
  cisco_user: 'Cisco',
  admin: 'Administrator',
  customer: 'Customer',
};

function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [status, setStatus] = useState('validating'); // validating | valid | invalid | expired | accepted | accepting | error
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);

  // Validate the token on mount
  useEffect(() => {
    async function validateToken() {
      try {
        const response = await apiClient.get(`/api/invitations/token/${token}`);
        if (response.valid) {
          setInvitation(response.invitation);
          setStatus('valid');
        }
      } catch (err) {
        const errorMsg = err.message || 'Invalid invitation';
        if (errorMsg.includes('expired')) {
          setStatus('expired');
        } else if (errorMsg.includes('already been used')) {
          setStatus('accepted');
        } else {
          setStatus('invalid');
        }
        setError(errorMsg);
      }
    }

    if (token) {
      validateToken();
    } else {
      setStatus('invalid');
      setError('No invitation token provided');
    }
  }, [token]);

  // Auto-accept if user is logged in and invitation is valid
  const handleAccept = async () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/login?returnTo=/invite/${token}`);
      return;
    }

    setStatus('accepting');
    try {
      const response = await apiClient.post(`/api/invitations/accept/${token}`);
      if (response.success) {
        setStatus('accepted');
        // Redirect to welcome page after a brief moment
        setTimeout(() => {
          navigate('/welcome');
        }, 2000);
      }
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Failed to accept invitation');
    }
  };

  // Render loading state
  if (status === 'validating' || authLoading) {
    return (
      <div className="invite-page">
        <div className="invite-card invite-card--loading">
          <Loader2 className="invite-spinner" size={48} />
          <p>Validating your invitation...</p>
        </div>
      </div>
    );
  }

  // Render invalid/expired states
  if (status === 'invalid' || status === 'expired') {
    return (
      <div className="invite-page">
        <div className="invite-card invite-card--error">
          <div className="invite-icon invite-icon--error">
            {status === 'expired' ? <Clock size={32} /> : <XCircle size={32} />}
          </div>
          <h1>{status === 'expired' ? 'Invitation Expired' : 'Invalid Invitation'}</h1>
          <p>
            {status === 'expired'
              ? 'This invitation link has expired. Please contact your administrator for a new invitation.'
              : 'This invitation link is not valid. Please check the link and try again.'}
          </p>
          <Link to="/login" className="invite-btn invite-btn--secondary">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // Render already accepted state
  if (status === 'accepted' && !invitation) {
    return (
      <div className="invite-page">
        <div className="invite-card invite-card--info">
          <div className="invite-icon invite-icon--info">
            <CheckCircle size={32} />
          </div>
          <h1>Invitation Already Used</h1>
          <p>This invitation has already been accepted. If this was you, you can sign in below.</p>
          <Link to="/login" className="invite-btn invite-btn--primary">
            <LogIn size={18} />
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  // Render accepting state
  if (status === 'accepting') {
    return (
      <div className="invite-page">
        <div className="invite-card invite-card--loading">
          <Loader2 className="invite-spinner" size={48} />
          <p>Setting up your account...</p>
        </div>
      </div>
    );
  }

  // Render success state
  if (status === 'accepted' && invitation) {
    return (
      <div className="invite-page">
        <div className="invite-card invite-card--success">
          <div className="invite-icon invite-icon--success">
            <CheckCircle size={48} />
          </div>
          <h1>Welcome to TekStak!</h1>
          <p>Your account has been set up with {ROLE_LABELS[invitation.role] || invitation.role} access.</p>
          <p className="invite-redirect">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (status === 'error') {
    return (
      <div className="invite-page">
        <div className="invite-card invite-card--error">
          <div className="invite-icon invite-icon--error">
            <XCircle size={32} />
          </div>
          <h1>Something Went Wrong</h1>
          <p>{error || 'Failed to accept the invitation. Please try again.'}</p>
          <button onClick={handleAccept} className="invite-btn invite-btn--primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Render valid invitation - prompt to accept
  return (
    <div className="invite-page">
      <div className="invite-card">
        <div className="invite-icon">
          <Shield size={48} />
        </div>

        <h1>You're Invited to TekStak</h1>

        <div className="invite-details">
          <div className="invite-detail">
            <span className="invite-detail-label">Invited Email</span>
            <span className="invite-detail-value">{invitation.email}</span>
          </div>
          <div className="invite-detail">
            <span className="invite-detail-label">Access Level</span>
            <span className="invite-detail-value invite-detail-value--role">
              {ROLE_LABELS[invitation.role] || invitation.role}
            </span>
          </div>
          <div className="invite-detail">
            <span className="invite-detail-label">Expires</span>
            <span className="invite-detail-value">
              {new Date(invitation.expiresAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
        </div>

        <div className="invite-features">
          <h3>What you'll get access to:</h3>
          <ul>
            <li>Import and manage your account lists</li>
            <li>Vendor Competitive Analysis dashboard</li>
            <li>Spending Flow visualization</li>
            <li>Zero Trust maturity assessment</li>
          </ul>
        </div>

        {user ? (
          <button onClick={handleAccept} className="invite-btn invite-btn--primary">
            Accept Invitation
            <ArrowRight size={18} />
          </button>
        ) : (
          <div className="invite-auth-options">
            <p className="invite-auth-prompt">Sign in or create an account to accept this invitation</p>
            <div className="invite-auth-buttons">
              <Link to={`/login?returnTo=/invite/${token}`} className="invite-btn invite-btn--primary">
                <LogIn size={18} />
                Sign In
              </Link>
              <Link to={`/signup?returnTo=/invite/${token}`} className="invite-btn invite-btn--secondary">
                <UserPlus size={18} />
                Create Account
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InviteAccept;
